import React from "react";
import { SECTIONS, SignalDef, SignalSection } from "../models/s7DbMap";
import { MachineStatusDto } from "../models/edgeMonitorModels";
import SignalTile from "./SignalTile";

const FONT_MONO = "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace";

const STALE_THRESHOLD_MS = 10_000;

/**
 * Map an `s7DbMap` key to a value pulled from `MachineStatusDto.signals`. If
 * `signals` is undefined (§13.2 not yet deployed), fall back to the 11
 * normalized fields where possible:
 *   - `programRunning` → derived from `status` ∈ { Running }
 *   - `spindleSpeedRpm` → `spindleSpeed`
 *   - `nckAlarmActive` → derived from `alarmCode != null`
 *
 * All other keys return undefined → render as null/empty per §3.2.3.
 */
const resolveValue = (
  key: string,
  status: MachineStatusDto | null
): unknown => {
  if (!status) return undefined;
  if (status.signals && key in status.signals) {
    return status.signals[key];
  }
  // Degraded-mode fallbacks — mapped from the 11 normalized fields.
  switch (key) {
    case "programRunning":
      return status.status === 1; // MachineStatusCode.Running
    case "spindleSpeedRpm":
      return status.spindleSpeed != null
        ? Number(status.spindleSpeed)
        : undefined;
    case "nckAlarmActive":
      return Boolean(status.alarmCode);
    default:
      return undefined;
  }
};

const tileBgFromSection = (
  num: number
):
  | "operational"
  | "safety"
  | "cycle"
  | "process"
  | "chuck"
  | "coolant"
  | "alarms" => {
  switch (num) {
    case 1:
      return "operational";
    case 2:
      return "safety";
    case 3:
      return "cycle";
    case 4:
      return "process";
    case 5:
      return "chuck";
    case 6:
      return "coolant";
    case 7:
      return "alarms";
    default:
      return "operational";
  }
};

const SectionHeader: React.FC<{ section: SignalSection; right?: string }> = ({
  section,
  right,
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      marginBottom: "12px",
      marginTop: "20px",
      paddingBottom: "4px",
    }}
  >
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: "12px",
        fontWeight: 600,
        color: "#3D3D3D",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      }}
    >
      {String(section.num).padStart(2, "0")} · {section.heading}
      <span
        style={{
          marginLeft: "12px",
          fontWeight: 400,
          color: "#9A9A8E",
          fontSize: "11px",
        }}
      >
        {section.visionRef}
        {" · "}
        {section.signals.length} SIGNAL{section.signals.length === 1 ? "" : "S"}
        {section.subtitle ? ` · ${section.subtitle}` : ""}
      </span>
    </div>
    {right && (
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: "11px",
          color: "#9A9A8E",
        }}
      >
        {right}
      </div>
    )}
  </div>
);

export interface Zone2SignalGridProps {
  status: MachineStatusDto | null;
  /** ISO timestamp of the most recent signalR push or REST poll. */
  lastDataAt: string | null;
}

const Zone2SignalGrid: React.FC<Zone2SignalGridProps> = ({
  status,
  lastDataAt,
}) => {
  const lastDataMs = lastDataAt ? Date.parse(lastDataAt) : null;
  const isStale = (signal: SignalDef): boolean => {
    if (signal.kind === "placeholder") return false;
    if (lastDataMs == null) return true; // never received any data yet → stale
    if (Date.now() - lastDataMs > STALE_THRESHOLD_MS) return true;
    // Per-tile staleness: if signals object exists but key missing AND we have
    // status, treat as stale (degraded mode for §13.2-pending fields).
    if (status?.signals) {
      // signals object present from §13 backend — key presence drives staleness.
      return !(signal.key in status.signals);
    }
    // No signals object: degraded mode. Only the 3 fallback-mapped keys are
    // fresh; the other 36 are stale.
    const fallbackKeys = ["programRunning", "spindleSpeedRpm", "nckAlarmActive"];
    return !fallbackKeys.includes(signal.key);
  };

  // Determine the active mode flag (only one of MODE_AUTO/MDA/JOG/REF should
  // bold). Resolve from values; first true wins. If none, none active.
  const modeFlags = ["modeAuto", "modeMda", "modeJog", "modeRef"];
  const activeModeKey = modeFlags.find(
    (k) => resolveValue(k, status) === true
  );

  // Compute a friendly per-section right-aligned subtitle.
  const sectionSubtitle = (section: SignalSection): string => {
    switch (section.num) {
      case 1: {
        const allFresh = section.signals.every((s) => !isStale(s));
        return allFresh ? "all fresh · last 2s" : "";
      }
      case 2: {
        // "all OK" if no bad-when-* signals are bad
        const anyBad = section.signals.some((s) => {
          const v = resolveValue(s.key, status);
          if (s.semantic === "bad-when-true" && v === true) return true;
          if (s.semantic === "bad-when-false" && v === false) return true;
          return false;
        });
        return anyBad ? "" : "all OK";
      }
      case 3:
        return "activeProgram null on 810D · Phase 2 OPC-UA";
      case 4:
        return "numeric · 1 Hz tick";
      case 5: {
        const closed = resolveValue("chuckClosed", status) === true;
        return closed ? "workpiece clamped" : "";
      }
      case 6:
        return "request → active → contactor";
      case 7: {
        const v = resolveValue("nckAlarmActive", status);
        return v === true ? "" : "no active alarms";
      }
      default:
        return "";
    }
  };

  return (
    <div style={{ width: "100%", paddingRight: "24px" }}>
      {SECTIONS.map((section) => (
        <div key={section.num}>
          <SectionHeader section={section} right={sectionSubtitle(section)} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "12px",
            }}
          >
            {section.signals.map((signal) => {
              const value = resolveValue(signal.key, status);
              const stale = isStale(signal);
              const isActiveMode =
                signal.modeFlag === true && signal.key === activeModeKey;
              return (
                <SignalTile
                  key={signal.key}
                  signal={signal}
                  value={value}
                  stale={stale}
                  isActiveMode={isActiveMode}
                  bgStyle={tileBgFromSection(section.num)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Zone2SignalGrid;
